import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { ToastService } from './toast.service';
import { UserImpersonationService } from './user-impersonation.service';

@Injectable({
  providedIn: 'root',
})
export class AssetNavigationService {
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);
  private readonly authService = inject(AuthService);
  private readonly impersonationService = inject(UserImpersonationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslocoService);

  async navigateToAsset(assetId: number): Promise<boolean> {
    const userIdSignal = this.authService.currentUserId;
    const realUserId = userIdSignal();
    const effectiveUserId = this.impersonationService.getEffectiveUserId(realUserId);

    if (!effectiveUserId) {
      this.toastService.error(
        this.translate.translate('common.toasts.userNotAuthenticated'),
        false,
        this.translate.translate('common.toasts.error'),
      );
      return false;
    }

    try {
      const firstAuthorizedRoute = await this.permissionService.getFirstAuthorizedRoute(effectiveUserId, assetId);

      if (firstAuthorizedRoute) {
        await this.router.navigate(['assets', assetId, ...firstAuthorizedRoute.split('/')]);
        return true;
      } else {
        this.toastService.error(
          this.translate.translate('common.toasts.noPermissionForAsset'),
          false,
          this.translate.translate('common.toasts.accessDenied'),
        );
        return false;
      }
    } catch (error) {
      console.error('Error during asset navigation:', error);
      this.toastService.error(
        this.translate.translate('common.toasts.navigationError'),
        false,
        this.translate.translate('common.toasts.technicalError'),
      );
      return false;
    }
  }
}
